/**
 * Shared election-mode chair script + next-step helpers.
 * Used by ElectionsInterface and Chair Dashboard so wording stays in sync.
 */

export const ELECTION_STAGES = [
  { id: 1, label: 'Setup' },
  { id: 2, label: 'Nominate' },
  { id: 3, label: 'Speeches' },
  { id: 4, label: 'Voting' },
  { id: 5, label: 'Results' },
];

export function getElectionOfficeLabel(currentElection, fallbackOffice = '') {
  return (
    String(currentElection?.office ?? fallbackOffice ?? '')
      .trim() || 'this office'
  );
}

export function getElectionWinners({
  electionScreen = 1,
  electionNominees = [],
  electionWriteInCandidates = [],
  electionBallotVotes = {},
  seatsToFill = 1,
} = {}) {
  if (electionScreen < 5) return [];

  const allCandidates = [
    ...electionNominees
      .filter((n) => n.status === 'accepted')
      .map((n) => ({ name: n.name, votes: electionBallotVotes[n.id] || 0 })),
    ...(Array.isArray(electionWriteInCandidates) ? electionWriteInCandidates : []),
  ];

  if (allCandidates.length === 0) return [];

  const sorted = [...allCandidates].sort((a, b) => b.votes - a.votes);
  return sorted.slice(0, Math.max(1, seatsToFill || 1));
}

/**
 * @returns {string} Spoken “Say this” line for the current election stage
 */
export function getElectionChairScript({
  electionScreen = 1,
  officeLabel = 'this office',
  electionNominees = [],
  currentCandidate = null,
  speechTimeLimit = 3,
  secretBallot = true,
  showVoteBreakdown = false,
  electionWriteInCandidates = [],
  electionBallotVotes = {},
  seatsToFill = 1,
} = {}) {
  const office = officeLabel || 'this office';
  const limit = Number(speechTimeLimit) || 3;
  const nominees = Array.isArray(electionNominees) ? electionNominees : [];

  switch (electionScreen) {
    case 1:
      return `We will now proceed to the election for ${office}. Nominations will follow shortly.`;

    case 2: {
      const acceptedNominees = nominees.filter((n) => n.status === 'accepted');
      if (nominees.length === 0) {
        return `Election for ${office} is now open for nominations. Are there nominations for ${office}?`;
      }
      if (acceptedNominees.length === 0) {
        return `We have received nominations for ${office}. Waiting for nominees to accept or decline their nominations.`;
      }
      return `We have ${acceptedNominees.length} accepted nominee${
        acceptedNominees.length > 1 ? 's' : ''
      } for ${office}: ${acceptedNominees.map((n) => n.name).join(', ')}. Are there any additional nominations? If not, the chair will close nominations and begin speeches.`;
    }

    case 3:
      if (currentCandidate) {
        return `The chair recognizes ${currentCandidate.name} for a speech of ${limit} minute${
          limit !== 1 ? 's' : ''
        } for ${office}.`;
      }
      {
        const remainingSpeakers = nominees.filter(
          (n) => n.status === 'accepted' && !n.hasSpoken
        );
        if (remainingSpeakers.length === 0) {
          return `All candidates have now addressed the assembly. The chair will now proceed to the voting phase for ${office}.`;
        }
        return `Nominations for ${office} are now closed. We will now hear speeches from the candidates. Each candidate will have ${limit} minute${
          limit > 1 ? 's' : ''
        } to address the assembly.`;
      }

    case 4:
      return `Speeches have concluded. We will now proceed to ${
        secretBallot ? 'secret ballot' : 'open'
      } voting for ${office}. Please prepare your ballots.`;

    case 5: {
      const winners = getElectionWinners({
        electionScreen,
        electionNominees: nominees,
        electionWriteInCandidates,
        electionBallotVotes,
        seatsToFill,
      });
      const winner = winners[0] || null;
      const includeBreakdown = Boolean(showVoteBreakdown);
      if (winner) {
        if (includeBreakdown) {
          return `The election results for ${office}: ${winner.name} has been elected as ${office}, with ${
            winner.votes
          } vote${winner.votes !== 1 ? 's' : ''}. Congratulations!`;
        }
        return `The election results for ${office}: ${winner.name} has been elected as ${office}. Congratulations!`;
      }
      return `The election for ${office} has concluded without a clear winner.`;
    }

    default:
      return 'Election in progress.';
  }
}

/**
 * @returns {{ title: string, detail: string }}
 */
export function getElectionNextStep({
  electionScreen = 1,
  officeLabel = 'this office',
  electionNominees = [],
  currentCandidate = null,
} = {}) {
  const office = officeLabel || 'this office';
  const nominees = Array.isArray(electionNominees) ? electionNominees : [];

  switch (electionScreen) {
    case 1:
      return {
        title: 'Set up the election',
        detail: 'Confirm the office and speech/ballot settings on the Driver console, then start nominations.',
      };
    case 2: {
      const accepted = nominees.filter((n) => n.status === 'accepted');
      if (nominees.length === 0) {
        return {
          title: 'Open nominations',
          detail: `Ask for nominations for ${office}. The secretary records each nominee on the Driver console.`,
        };
      }
      if (accepted.length === 0) {
        return {
          title: 'Confirm nominees',
          detail: 'Wait for nominees to accept or decline, then continue calling for further nominations.',
        };
      }
      return {
        title: 'Close nominations',
        detail: 'Ask if there are further nominations. When none, close nominations and move to speeches.',
      };
    }
    case 3:
      if (currentCandidate) {
        return {
          title: 'Candidate speaking',
          detail: `${currentCandidate.name} has the floor. Keep time; advance when the speech ends.`,
        };
      }
      return {
        title: 'Candidate speeches',
        detail: 'Recognize each accepted nominee in turn for their timed speech.',
      };
    case 4:
      return {
        title: 'Conduct the ballot',
        detail: `Oversee voting for ${office}. The secretary tallies on the Driver console.`,
      };
    case 5:
      return {
        title: 'Announce the result',
        detail: `Declare the outcome for ${office}, congratulate the winner, then return to regular business.`,
      };
    default:
      return {
        title: 'Election in progress',
        detail: 'Follow the Driver console stage and keep the assembly informed.',
      };
  }
}
